import React from 'react';
import classNames from 'classnames';
import styles from './timesheet-row-preview.module.scss';
import moment from 'moment';
import {getTimeBankCellTitle, TimeBankCellStripe, TimeBankLabel} from './cells';

export type TimesheetRowAction = {
    id: string;
    icon?: React.ReactNode;
    label: string;
    onClick?: () => void | Promise<void>;
    disabled?: boolean;
    active?: boolean;
    render?: React.ReactNode;
    variant?: 'approve' | 'billable';
};

export type TimesheetRowPreviewProps = {
    date: string;
    name: string;
    type: string;
    value: string | number;
    timeBankLabel?: TimeBankLabel;
    icon?: React.ReactNode;
    actions?: TimesheetRowAction[];
    className?: string;
};

const formatMobileTimesheetDate = (value: string): React.ReactNode => {
    const date = moment(value, 'YYYY-MM-DD', true);

    if (!date.isValid()) {
        return value;
    }

    return (
        <span className={styles.dateValue}>
            <span className={styles.dateWeekday}>{date.format('dd')}</span>
            <span>{date.format('L')}</span>
        </span>
    );
};

const TimesheetRowPreview: React.FC<TimesheetRowPreviewProps> = ({
    date,
    name,
    type,
    value,
    timeBankLabel,
    icon,
    actions = [],
    className
}) => (
    <div className={classNames(styles.row, className)}>
        <div className={styles.infoSection}>
            {icon ? <span className={styles.iconSlot}>{icon}</span> : null}

            <div className={styles.textColumn}>
                <span className={styles.date}>{formatMobileTimesheetDate(date)}</span>
                <span className={styles.name} title={name}>{name}</span>
                <span className={styles.type}>
                    <span className={styles.typeText} title={type}>{type}</span>
                </span>
            </div>

            <span className={styles.value} title={timeBankLabel ? getTimeBankCellTitle(timeBankLabel) : undefined}>
                <span className={styles.valueNumber}>{value}</span>
                {timeBankLabel ? (
                    <TimeBankCellStripe label={timeBankLabel} className={styles.timeBankStripe} />
                ) : null}
            </span>
        </div>

        <div className={styles.actionsSection}>
            {actions.map((action) => (
                <div key={action.id} className={styles.actionCell}>
                    {action.render ?? (
                        <button
                            type='button'
                            className={classNames(styles.actionButton, {
                                [styles.actionButtonActive]: action.active,
                                [styles.actionButtonApprove]: action.variant === 'approve' || action.id === 'approve',
                                [styles.actionButtonBillable]: action.variant === 'billable' || action.id === 'billable'
                            })}
                            aria-label={action.label}
                            title={action.label}
                            onClick={action.onClick}
                            disabled={action.disabled}
                        >
                            <span className={styles.actionIcon}>{action.icon}</span>
                        </button>
                    )}
                </div>
            ))}
        </div>
    </div>
);

export default TimesheetRowPreview;
